/*
 * This custom hook is used to manage the dealership picker functionality.
 * It fetches dealership data, filters it based on the common organization ID,
 * and provides the necessary data for the dealership selector component.
 */

// externals
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internals
import { dealershipSelectors, fetchDealerships } from '../store';
import { useDealershipSelector } from '../selectors/useDealershipSelector';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import type { DealershipPickerMode } from '@makemydeal/dr-shared-types';

import { useFilteredDealerships } from './useFilteredDealerships';

export const useDealershipPicker = (mode: DealershipPickerMode) => {
    const dispatch = useDispatch();
    const { getDealershipURL, getAvailableDealerships } = dealershipSelectors.additionalSelectors;
    const { requestStatus } = useDealershipSelector();

    // Selectors to fetch the BFF endpoint and available dealerships from the Redux store.
    const bffEndpoint = useSelector(getDealershipURL);
    const availableDealerships = useSelector(getAvailableDealerships ?? (() => []));

    // Use the filtering hook to get filtered dealerships and auto-selection logic
    const { dealershipsArr, selectedDealership } = useFilteredDealerships(availableDealerships, mode);

    // Define the loading states for the API request.
    const loadingStates: ApiRequestStatus[] = [ApiRequestStatus.Loading, ApiRequestStatus.Idle];

    // useEffect to fetch dealership data when the BFF endpoint is available and the request status is idle.
    useEffect(() => {
        if (bffEndpoint && requestStatus == ApiRequestStatus.Idle && availableDealerships.length === 0) {
            // Dispatch an action to fetch dealerships from the BFF endpoint.
            dispatch(fetchDealerships(bffEndpoint));
        }
    }, [dispatch, bffEndpoint, requestStatus, availableDealerships.length]);

    // Return the processed dealership data and state for the dealership picker component.
    return {
        dealerships: dealershipsArr, // List of filtered dealerships for the selector.
        selectedDealership, // Automatically selected dealership (if applicable).
        shouldDisableSelector: selectedDealership !== undefined, // Disable selector if a dealership is pre-selected.
        isLoading: availableDealerships.length === 0 && loadingStates.includes(requestStatus as ApiRequestStatus) // Loading state for the dealership data.
    };
};
